import { emitter, greaterThanToday, parseDate } from "@theprelude/core";
import { createJSONStorage, persist } from "zustand/middleware";
import { createWithEqualityFn as create } from "zustand/traditional";

interface ThreatIntelStore {
  edits: ThreatIntelEdit[];
  setEdit: (newEdit: ThreatIntelEdit) => void;
  clearEdits: (jobId: string) => void;
  resetEdit: (name: string) => void;
  clearExpiredEdits: () => void;
}

// Naming is, but not limited to as follows: {job_id}_{technique}_{file_name}
// Ex: 26624c3b-f2be-43e6-93ac-7743b82df184_T1005_sigma_rule_1

export interface ThreatIntelEdit {
  name: string;
  technique: string;
  content: string;
  expiration: string;
}

function updateJob(job: ThreatIntelEdit, newContent: string): ThreatIntelEdit {
  return { ...job, content: newContent };
}

export const useThreatIntelStore = create<ThreatIntelStore>()(
  persist(
    (set) => ({
      edits: [],
      setEdit(newJob) {
        set((state) => {
          const existingJob = state.edits.find(
            (edit) => edit.name === newJob.name,
          );
          if (existingJob) {
            const filteredEdits = state.edits.filter(
              (edit) => edit.name !== newJob.name,
            );
            const jobToAdd = updateJob(existingJob, newJob.content);
            const updatedJobs = [...filteredEdits, jobToAdd];
            return { edits: updatedJobs };
          }
          return { edits: [...state.edits, newJob] };
        });
      },
      clearEdits(id) {
        set((state) => {
          const filteredEdits = state.edits.filter(
            (edit) => !edit.name.includes(id),
          );
          return { edits: [...filteredEdits] };
        });
      },
      resetEdit(name) {
        set((state) => {
          const filteredEdits = state.edits.filter(
            (edit) => edit.name !== name,
          );
          return { edits: [...filteredEdits] };
        });
      },
      clearExpiredEdits() {
        set((state) => {
          const filteredEdits = state.edits.filter(
            (edit) => greaterThanToday(parseDate(edit.expiration), 0) === false,
          );
          return { edits: [...filteredEdits] };
        });
      },
    }),
    {
      name: `prelude:threat-intel`,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

emitter.addEventListener("auth:switch", () => {
  useThreatIntelStore.setState({
    edits: [],
  });
});
